import { Button } from "@/components/ui/button";

type FilterChipBarProps = {
  /** Distinct option labels, already in the order they should appear. */
  options: string[];
  /** Selected options (empty = no filter, show all). */
  selected: ReadonlySet<string>;
  onToggle: (option: string) => void;
  onClear: () => void;
  /** Heading above the chips. Omit where the surrounding card already names the list. */
  title?: string;
  /** Prefix for the per-chip test ids (`<prefix>-<option>`, `<prefix>-clear`). */
  testIdPrefix: string;
};

/**
 * The multi-select chip row behind every "narrow this list down" control on
 * the schedule screens — by stage and by crew function, in both the creator
 * and the read-only timeline. Renders nothing for 0 or 1 option, since
 * filtering a single value is meaningless.
 *
 * Deliberately domain-free: callers derive `options` with the matching lib
 * helper (listStageLabels / listCrewFunctions), so adding a third filter
 * needs no change here.
 */
export function FilterChipBar({
  options,
  selected,
  onToggle,
  onClear,
  title,
  testIdPrefix,
}: FilterChipBarProps) {
  if (options.length <= 1) return null;

  return (
    <div className="mb-4">
      {title && <h3 className="mb-2 font-semibold">{title}</h3>}
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <Button
            key={option}
            variant={selected.has(option) ? "default" : "outline"}
            size="sm"
            onClick={() => onToggle(option)}
            data-testid={`${testIdPrefix}-${option}`}
          >
            {option}
          </Button>
        ))}
        {selected.size > 0 && (
          <Button variant="ghost" size="sm" onClick={onClear} data-testid={`${testIdPrefix}-clear`}>
            Clear
          </Button>
        )}
      </div>
    </div>
  );
}
